/**
 * Shared /info cooldown (#1809). Hyperliquid's REST weight is per IP, so after one 429 every other read would fail too.
 * One InfoCooldown per network (HealthMonitor owns it): after a 429 no /info request is sent until the cooldown ends.
 *
 * - Length: Retry-After when the 429 carries a usable one (up to maxRetryAfterMs, a guard against a bad header); else
 *   exponential from baseMs with equal jitter, capped at maxMs. A later success ends the streak.
 * - Requests answered 429 together are one event: they do not escalate, and a request sent before a 429 came back
 *   cannot clear it with its own success.
 * - Waiting requests are released over spreadMs, not all at once, and go back to waiting if another 429 lands meanwhile.
 *
 * Reads only. /exchange (orders, cancels) never goes through this: postExchange() fires at once and is never retried.
 * It covers one tab; other tabs on the same IP learn about the limit from their own 429.
 */
import { defaultSleep, retryAfterMs } from './info';

export interface InfoCooldownOptions {
  baseMs?: number;
  maxMs?: number;
  maxRetryAfterMs?: number;
  spreadMs?: number;
  now?: () => number;
  random?: () => number;
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
  /** Called each time a 429 starts or extends a cooldown. */
  onHit?: () => void;
}

export class InfoCooldown {
  private readonly baseMs: number;
  private readonly maxMs: number;
  private readonly maxRetryAfterMs: number;
  private readonly spreadMs: number;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly sleep: (ms: number, signal: AbortSignal) => Promise<void>;
  private readonly onHit: (() => void) | undefined;
  private until = 0;
  private streak = 0;
  private lastHitAt = Number.NEGATIVE_INFINITY;

  constructor(opts: InfoCooldownOptions = {}) {
    this.baseMs = opts.baseMs ?? 10_000;
    this.maxMs = opts.maxMs ?? 60_000;
    this.maxRetryAfterMs = opts.maxRetryAfterMs ?? 300_000;
    this.spreadMs = opts.spreadMs ?? 2_000;
    this.now = opts.now ?? Date.now;
    this.random = opts.random ?? Math.random;
    this.sleep = opts.sleep ?? defaultSleep;
    this.onHit = opts.onHit;
  }

  get remainingMs(): number {
    return Math.max(0, this.until - this.now());
  }

  get active(): boolean {
    return this.remainingMs > 0;
  }

  /** A request sent at `sentAt` was answered 429. */
  hit(sentAt: number, retryAfter?: number): void {
    const honoured = retryAfter ? Math.min(retryAfter, this.maxRetryAfterMs) : undefined;
    // Sent no later than the last 429 came back: it could not have seen the cooldown, so it is the same burst.
    const sameBurst = sentAt <= this.lastHitAt;
    const t = this.now();
    this.lastHitAt = t;
    if (!sameBurst) this.streak++;
    else if (honoured === undefined) return;
    const cap = Math.min(this.maxMs, this.baseMs * 2 ** (this.streak - 1));
    const backoff = cap / 2 + this.random() * (cap / 2);
    this.until = Math.max(this.until, t + (honoured ?? backoff));
    this.onHit?.();
  }

  /** A request sent at `sentAt` succeeded. Only one sent after the last 429 proves the limit has passed. */
  clear(sentAt: number): void {
    if (sentAt > this.lastHitAt) this.streak = 0;
  }

  /** Resolves when a request may be sent. Rejects with an AbortError when `signal` aborts. */
  async ready(signal: AbortSignal): Promise<void> {
    for (let left = this.remainingMs; left > 0; left = this.remainingMs) {
      await this.sleep(left + this.random() * this.spreadMs, signal);
    }
  }

  /** `base` behind this cooldown: waits, sends, records the answer. The wait is not an attempt, so wrap outside any timer. */
  wrapFetch(base: typeof fetch): typeof fetch {
    return async (input, init) => {
      if (this.active) await this.ready(init?.signal ?? new AbortController().signal);
      const sentAt = this.now();
      const res = await base(input, init);
      if (res.status === 429) this.hit(sentAt, retryAfterMs(res));
      else if (res.ok) this.clear(sentAt);
      return res;
    };
  }
}
